import type { LocalizedText } from './api'

export function pickLocalized(text: LocalizedText, language: string): string {
  return language.toLowerCase().startsWith('en') ? text.en : text.zh
}

function formatterLocale(language: string): string {
  return language.toLowerCase().startsWith('en') ? 'en-US' : 'zh-TW'
}

/** Explicit UTC+08:00 style zone label (SDD v3 §2.2): "GMT+8" is shown on every time. */
export function formatEventTime(iso: string, timezone: string, language: string): string {
  return new Intl.DateTimeFormat(formatterLocale(language), {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso))
}

export function formatEventDate(iso: string, timezone: string, language: string): string {
  return new Intl.DateTimeFormat(formatterLocale(language), {
    timeZone: timezone,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso))
}

export function formatEventRange(
  startsAt: string,
  endsAt: string,
  timezone: string,
  language: string,
): string {
  return `${formatEventDate(startsAt, timezone, language)} - ${formatEventDate(endsAt, timezone, language)} (UTC+08:00)`
}

/** Shuttle reservations and cancellations close this long before departure (mirrors the API's SHUTTLE_CUTOFF_HOURS). */
export const SHUTTLE_CUTOFF_HOURS = 12

export function shuttleDeadlineMs(departsAt: string): number {
  return Date.parse(departsAt) - SHUTTLE_CUTOFF_HOURS * 3_600_000
}

/** Whole minutes left, rounded up so "18h 0m" is shown rather than "17h 59m" right after a refresh. */
export function remainingParts(untilMs: number, nowMs: number) {
  const minutes = Math.max(0, Math.ceil((untilMs - nowMs) / 60_000))
  return {
    closed: untilMs - nowMs <= 0,
    days: Math.floor(minutes / 1440),
    hours: Math.floor((minutes % 1440) / 60),
    minutes: minutes % 60,
  }
}

/** `YYYY-MM-DD` of a moment in Taipei time (service dates are Taipei calendar days). */
export function taipeiDate(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms))
}

/** Today if it has service, else the next upcoming service day, else the last one. */
export function defaultServiceDay(days: string[], today: string): string {
  const sorted = [...days].sort()
  return sorted.find((d) => d >= today) ?? sorted[sorted.length - 1] ?? ''
}
